<template>
  <DynamicDialog
    :show="dialog"
    @dialogOk="setDateRange()"
    @dialogCancel="dialog = false"
    @update:showDyamicDialog="(state) => (dialog = state)"
    max-width="700"
    min-height="400"
    okBtnColor="success"
    cancelBtnColor="none"
    cancelBtnVariant="text"
    :okBtnDisabled="okBtnDisabled"
  >
    <template v-slot:title>Select date range</template>
    <template v-slot:content>
      <v-row v-if="allowDays" justify="center">
        <v-col cols="6" class="text-left">
          <v-date-picker v-model="startDate" title="From"></v-date-picker>
        </v-col>
        <v-divider vertical />
        <v-col cols="6" class="text-left">
          <v-date-picker v-model="endDate" title="To"></v-date-picker>
        </v-col>
      </v-row>

      <v-row v-else justify="center">
        <v-col cols="6" class="text-left">
          <YearMonthPicker v-model="startDate" headerText="FROM" />
        </v-col>
        <v-divider vertical />
        <v-col cols="6" class="text-left">
          <YearMonthPicker v-model="endDate" headerText="TO" />
        </v-col>
      </v-row>
    </template>
    <template v-slot:okBtnTitle>set date range</template>
  </DynamicDialog>
</template>
<script>
import { systemMessages } from "@/mixins/SystemMessages";
import DynamicDialog from "@/components/DynamicDialog.vue";
import YearMonthPicker from "@/components/YearMonthPicker.vue";

export default {
  name: "DateRangePicker",
  mixins: [systemMessages],
  data: () => ({
    months: {
      1: "Jan",
      2: "Feb",
      3: "Mar",
      4: "Apr",
      5: "May",
      6: "Jun",
      7: "Jul",
      8: "Aug",
      9: "Sep",
      10: "Oct",
      11: "Nov",
      12: "Dec",
    },
    startDate: null,
    endDate: null,
  }),
  components: {
    DynamicDialog,
    YearMonthPicker,
  },
  props: {
    dialogProp: {
      type: Boolean,
      required: true,
      default: false,
    },
    // check if start date is greater than end date
    restrictGreaterStart: {
      type: Boolean,
      default: true,
    },
    allowDays: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    dialog: {
      get() {
        return this.dialogProp;
      },
      set(val) {
        this.$emit("update:dialogProp", val);
      },
    },
    okBtnDisabled() {
      if (this.allowDays) {
        return !this.startDate || !this.endDate;
      } else {
        return !this.startDate?.year || !this.endDate?.year;
      }
    },
  },
  methods: {
    /**
     * Serves the date range up the the parent component
     * @example { dateSelection: "2022-06 -> 2024-08", label: "Jun 2022 - Aug 2024" }
     */
    setDateRange() {
      const valid = this.validateSelection();

      if (valid) {
        // days need special handling
        if (this.allowDays) {
          this.handleDaySelection();
        }

        const dateSelection = `${this.startDate.sqlVersion} -> ${this.endDate.sqlVersion}`;
        const label = `${this.startDate.label} - ${this.endDate.label}`;

        this.$emit("setRange", { dateSelection, label });

        this.dialog = false;
      }
    },
    /* 
       If user selection needs any validation it can go into this method
    */
    validateSelection() {
      let isValid = true;

      if (this.restrictGreaterStart) {
        isValid = this.isStartDateLessThanEndDate();
      }
      // feel free to add more validation underneath

      return isValid;
    },
    isStartDateLessThanEndDate() {
      if (this.allowDays) {
        const start = new Date(this.startDate);
        const end = new Date(this.endDate);

        // check if start day is greater than end day
        if (start > end) {
          this.warningPill({
            title: "Start date cannot be greater than end date",
            icon: "mdi-information",
          });
          return false;
        }
      } else {
        const isStartGreaterThanEnd =
          this.startDate.year > this.endDate.year ||
          (this.startDate.year === this.endDate.year &&
            this.startDate.month > this.endDate.month);

        if (isStartGreaterThanEnd) {
          this.warningPill({
            title: "Start date cannot be greater than end date",
            icon: "mdi-information",
          });
          return false;
        }
      }

      return true;
    },
    handleDaySelection() {
      const pad = (n) => String(n).padStart(2, "0");
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);

      const startDateString = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
      const endDateString = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;

      const startDateLabel = `${this.months[start.getMonth() + 1]} ${pad(start.getDate())} ${start.getFullYear()}`;
      const endDateLabel = `${this.months[end.getMonth() + 1]} ${pad(end.getDate())} ${end.getFullYear()}`;
      const startDateSQLVersion = startDateString;
      const endDateSQLVersion = endDateString;

      this.startDate = {
        date: startDateString,
        label: startDateLabel,
        sqlVersion: startDateSQLVersion,
      };
      this.endDate = {
        date: endDateString,
        label: endDateLabel,
        sqlVersion: endDateSQLVersion,
      };
    },
  },
  watch: {
    dialog(open) {
      if (open) {
        // reset these
        this.startDate = null;
        this.endDate = null;
      }
    },
  },
};
</script>
<style scoped></style>
